/* eslint-disable react/no-unstable-nested-components */
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  Box,
  Chip,
  Stack,
  Button,
  MenuItem,
  TextField,
  Typography,
  CardContent,
  Autocomplete,
  ToggleButton,
  InputAdornment,
  ToggleButtonGroup,
} from '@mui/material';

import {
  getAllAutomationList,
  getAllRuleTemplateList,
  getAutomationFindingsList,
} from 'src/server/store/automation';

import Iconify from 'src/components/iconify';

import { redirectLinks } from './AutomationLogFilters';

// ----------------------------------------------------------------------

export default function ExecutionLogFilters({
  setFilterObject,
  getSearchPlaceholder,
  searchQuery,
  setSearchQuery,
  sortValue,
  setSortValue,
  sort,
}) {
  const { allRuleList, allAutomationList, allAutomationFindingsList } = useSelector((state) => ({
    allRuleList: state?.automation?.allRuleTemplateList?.automationRuleTemplates,
    allAutomationList: state?.automation?.allAutomationList?.automation,
    allAutomationFindingsList: state?.automation?.automationFindingsList?.automationFindings,
  }));

  const dispatch = useDispatch();

  const [ruleSearch, setRuleSearch] = useState('');
  const [automationSearch, setAutomationSearch] = useState('');
  const [findingsSearch, setFindingsSearch] = useState('');

  useEffect(() => {
    const credentials = {
      page: 1,
      limit: 10,
      search: ruleSearch,
      dispatch,
    };
    dispatch(getAllRuleTemplateList(credentials));
  }, [dispatch, ruleSearch]);

  useEffect(() => {
    const credentials = {
      page: 1,
      limit: 10,
      search: automationSearch,
      dispatch,
    };
    dispatch(getAllAutomationList({ credentials }));
  }, [dispatch, automationSearch]);

  useEffect(() => {
    const credentials = {
      page: 1,
      limit: 10,
      search: findingsSearch,
      dispatch,
    };
    dispatch(getAutomationFindingsList({ credentials }));
  }, [dispatch, findingsSearch]);

  const filteredAllRules = [
    ...(allRuleList || []).map((results) => ({
      name: results?.title,
      value: results?._id,
    })),
  ];

  const SORT_OPTIONS = [
    { value: -1, label: 'Latest' },
    { value: 1, label: 'Oldest' },
  ];

  const executionTypeOptions = [
    { label: 'All', value: '', color: 'success' },
    { label: 'Manual', value: 'MANUAL', color: 'success' },
    { label: 'Automatic', value: 'AUTOMATIC', color: 'success' },
  ];

  const executionStatusOptions = [
    { label: 'All', value: '', color: 'success' },
    { label: 'Pending', value: 'PENDING', color: 'info' },
    // { label: 'In Progress', value: 'IN_PROGRESS', color: 'info' },
    { label: 'Success', value: 'SUCCESS', color: 'success' },
    // { label: 'Failed', value: 'FAILED', color: 'error' },
    { label: 'Rejected', value: 'REJECTED', color: 'error' },
  ];

  const automationOptions = [
    ...(allAutomationList || []).map((automation) => ({
      name: automation?.title || automation?.name || 'Untitled Automation',
      value: automation?._id,
    })),
  ];
  const findingsOptions = [
    ...(allAutomationFindingsList || []).map((finding) => ({
      name: finding?.title || finding?.name || 'Untitled Finding',
      value: finding?._id,
    })),
  ];

  const campaignUpdateSchema = Yup.object().shape({
    automation: Yup.array(),
    findings: Yup.array(),
    automationRuleTemplate: Yup.array(),
    executionType: Yup.string(),
    executionStatus: Yup.string(),
  });

  const defaultValues = useMemo(
    () => ({
      automation: [],
      findings: [],
      automationRuleTemplate: [],
      executionType: '',
      executionStatus: '',
    }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(campaignUpdateSchema),
    defaultValues,
  });

  const {
    setValue,
    watch,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  console.log('errors', errors);
  console.log('Current form values:', watch());
  console.log('Current automation options:', automationOptions);
  console.log('Current findings options:', findingsOptions);
  console.log('Current rule options:', filteredAllRules);

  const onSubmit = handleSubmit((filterFromData) => {
    console.log('Filter form data:', filterFromData);

    // Define all possible filter fields
    const allFilterFields = [
      'automation',
      'findings',
      'automationRuleTemplate',
      'executionType',
      'executionStatus',
    ];

    // Filter out empty values and format the data
    const cleanedData = Object.keys(filterFromData)
      .filter((key) => allFilterFields.includes(key))
      .reduce((acc, key) => {
        const value = filterFromData[key];

        // Handle arrays (multiple selections)
        if (Array.isArray(value)) {
          if (value.length > 0) {
            acc[key] = { $in: value };
          }
        }
        // Handle string values (single selections)
        else if (typeof value === 'string' && value !== '') {
          acc[key] = value;
        }
        // Handle boolean values
        else if (typeof value === 'boolean') {
          acc[key] = value;
        }

        return acc;
      }, {});

    console.log('Cleaned filter data:', cleanedData);
    console.log('Setting filter object with:', cleanedData);
    setFilterObject(cleanedData);
  });

  // reset form
  const [resetKey, setResetKey] = useState(0);
  const handleResetFrom = () => {
    console.log('Resetting form to default values:', defaultValues);
    methods.reset(defaultValues);
    setFilterObject({});
    setResetKey((prevKey) => prevKey + 1);
  };

  const ToggleFilter = ({ label, name, options }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        value={watch(name) || ''}
        onChange={(event, newValue) => setValue(name, newValue)}
        exclusive
        size="small"
        sx={{ flexWrap: 'wrap', gap: 0.5 }}
      >
        {options.map((option) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            sx={{
              borderRadius: 2,
              px: 1.5,
              py: 0.3,
              '&.Mui-selected': {
                backgroundColor: `${option.color}.main`,
                color: 'white',
                '&:hover': {
                  backgroundColor: `${option.color}.dark`,
                },
              },
            }}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
  ToggleFilter.propTypes = {
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
  };

  const ChipFilter = ({ label, name, options }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {options.map((option) => (
          <Chip
            key={option.value}
            label={option.label}
            size="small"
            clickable
            variant={watch(name) === option.value ? 'filled' : 'outlined'}
            color={watch(name) === option.value ? option.color : 'default'}
            onClick={() => {
              const current = watch(name);
              const newValue = current === option.value ? '' : option.value;
              setValue(name, newValue);
            }}
            sx={{
              borderRadius: 2,
              '&:hover': {
                backgroundColor: `${option.color}.light`,
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
  ChipFilter.propTypes = {
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
  };

  const FilterFooter = () => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1}
      sx={{ mt: 0, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
    >
      <Stack direction="row" spacing={1}>
        <Stack width="fit-content" py={0} px={3} direction="row" alignItems="center" spacing={0.6}>
          <FilterAltIcon fontSize="small" sx={{ fontSize: '10px' }} />
          <Typography
            variant="caption"
            color="text.disabled"
            textAlign="center"
            sx={{ fontSize: '10px' }}
          >
            {/* Once you modify any of these filter values, make sure to apply those filters by clicking
            the Apply Filters Button */}
            Adjust filters and click Apply Filters Button
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          sx={{ px: 2, borderRadius: 2 }}
          onClick={handleResetFrom}
        >
          Reset All
        </Button>
        <LoadingButton
          color="primary"
          type="submit"
          size="small"
          variant="contained"
          loading={isSubmitting}
          sx={{ borderRadius: 2, px: 2 }}
        >
          Apply Filters
        </LoadingButton>
      </Stack>
    </Stack>
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <CardContent sx={{ px: 0, pt: 1, mt: 0 }} className="remove-padding-bottom-card">
          {redirectLinks}
          <Stack spacing={3}>
            {/* Search + Sort */}
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
              <TextField
                className="search-field-normal"
                size="small"
                placeholder={getSearchPlaceholder()}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 350, '& .MuiInputBase-root': { borderRadius: 2 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                value={sortValue}
                onChange={(e) => setSortValue(e.target.value)}
                label="Sort"
                sx={{ minWidth: 150, '& .MuiInputBase-root': { borderRadius: 2 } }}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                value={watch('executionStatus') || ''}
                onChange={(e) => setValue('executionStatus', e.target.value)}
                label="Execution Status"
                sx={{ minWidth: 150, '& .MuiInputBase-root': { borderRadius: 2 } }}
              >
                {executionStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <Autocomplete
                multiple
                key={resetKey}
                id="automation-select-demo"
                options={automationOptions}
                autoHighlight
                size="small"
                getOptionLabel={(option) => option.name}
                value={automationOptions.filter((option) =>
                  watch('automation')?.includes(option.value)
                )}
                onChange={(event, value) =>
                  setValue(
                    'automation',
                    value.map((item) => item.value)
                  )
                }
                renderOption={(props, option) => (
                  <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                    {option.name}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Automation"
                    name="automation"
                    className="autocomplete-custom"
                    placeholder="Select Automation"
                    onChange={(e) => setAutomationSearch(e.target.value)}
                    inputProps={{ ...params.inputProps, autoComplete: 'new-password' }}
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: 2,
                      },
                      minWidth: '200px',
                    }}
                  />
                )}
              />

              <Stack />

              <Autocomplete
                multiple
                key={resetKey}
                id="findings-select-demo"
                options={findingsOptions}
                autoHighlight
                size="small"
                getOptionLabel={(option) => option.name}
                value={findingsOptions.filter((option) =>
                  watch('findings')?.includes(option.value)
                )}
                onChange={(event, value) =>
                  setValue(
                    'findings',
                    value.map((item) => item.value)
                  )
                }
                renderOption={(props, option) => (
                  <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                    {option.name}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Findings"
                    name="findings"
                    className="autocomplete-custom"
                    placeholder="Select Findings"
                    onChange={(e) => setFindingsSearch(e.target.value)}
                    inputProps={{ ...params.inputProps, autoComplete: 'new-password' }}
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: 2,
                      },
                      minWidth: '200px',
                    }}
                  />
                )}
              />
              <Autocomplete
                multiple
                key={resetKey}
                id="rule-select-demo"
                options={filteredAllRules}
                autoHighlight
                size="small"
                getOptionLabel={(option) => option.name}
                value={filteredAllRules.filter((option) =>
                  watch('automationRuleTemplate')?.includes(option.value)
                )}
                onChange={(event, value) =>
                  setValue(
                    'automationRuleTemplate',
                    value.map((item) => item.value)
                  )
                }
                renderOption={(props, option) => (
                  <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                    {option.name}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rule"
                    name="automationRuleTemplate"
                    className="autocomplete-custom"
                    placeholder="Select Rule"
                    onChange={(e) => setRuleSearch(e.target.value)}
                    inputProps={{ ...params.inputProps, autoComplete: 'new-password' }}
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: 2,
                      },
                      minWidth: '200px',
                    }}
                  />
                )}
              />
            </Stack>

            <>
              {/* Filter Grid */}
              <Stack direction="row" alignItems="center" spacing={2}>
                <ToggleFilter
                  label="Execution Type"
                  name="executionType"
                  options={executionTypeOptions}
                />
              </Stack>
              <FilterFooter />
            </>
          </Stack>
        </CardContent>
      </form>
    </FormProvider>
  );
}

ExecutionLogFilters.propTypes = {
  sort: PropTypes.string,
  showHide: PropTypes.bool,
  setFilterObject: PropTypes.func,
  getSearchPlaceholder: PropTypes.func,
  searchQuery: PropTypes.string,
  setSearchQuery: PropTypes.func,
  sortValue: PropTypes.string,
  setSortValue: PropTypes.func,
};
